import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search, CalendarDays, Mail, ShieldAlert, ArrowLeft } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { LOGO_MARK } from "../components/site/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Login = () => (
  <div className="min-h-screen flex items-center justify-center bg-bone px-6" data-testid="dashboard-login">
    <div className="max-w-md w-full text-center">
      <img src={LOGO_MARK} alt="tpj" className="h-20 mx-auto" />
      <h1 className="font-serif text-4xl md:text-5xl mt-6 tracking-tight">Team Dashboard</h1>
      <p className="mt-4 text-ink/60">Sign in with Google to view consultation bookings and inquiries.</p>
      <button onClick={startLogin} data-testid="google-login-btn"
        className="mt-8 bg-terra text-white px-8 py-4 text-sm tracking-wide hover:bg-ink transition-colors duration-300">
        Sign in with Google
      </button>
      <a href="/" className="mt-6 block text-sm text-ink/50 hover:text-terra transition-colors">← Back to site</a>
    </div>
  </div>
);

const Denied = ({ onLogout }) => (
  <div className="min-h-screen flex items-center justify-center bg-bone px-6" data-testid="dashboard-denied">
    <div className="max-w-md w-full text-center">
      <ShieldAlert className="w-12 h-12 text-terra mx-auto" />
      <h1 className="font-serif text-3xl md:text-4xl mt-6">Access restricted</h1>
      <p className="mt-4 text-ink/60">This account isn't authorised to view the dashboard. Please sign in with the owner account.</p>
      <button onClick={onLogout} className="mt-8 border border-ink/25 px-6 py-3 text-sm hover:border-terra hover:text-terra transition-colors">Sign out</button>
    </div>
  </div>
);

const Card = ({ children, testid }) => (
  <div data-testid={testid} className="border border-ink/15 bg-bone p-6 hover:border-terra/40 transition-colors">{children}</div>
);

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [q, setQ] = useState("");
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!user || !user.is_admin) return;
    Promise.all([
      axios.get(`${API}/bookings`, { withCredentials: true }),
      axios.get(`${API}/inquiries`, { withCredentials: true }),
    ]).then(([b, i]) => {
      setBookings(b.data);
      setInquiries(i.data);
      setFetched(true);
    }).catch(() => toast.error("Could not load data"));
  }, [user]);

  const filter = (list) => {
    if (!q.trim()) return list;
    const s = q.toLowerCase();
    return list.filter((x) =>
      [x.name, x.email, x.organisation, x.service, x.message, x.notes, x.date, x.time]
        .filter(Boolean).some((v) => v.toLowerCase().includes(s)));
  };

  const fBookings = useMemo(() => filter(bookings), [bookings, q]);
  const fInquiries = useMemo(() => filter(inquiries), [inquiries, q]);

  const doLogout = async () => { await logout(); navigate("/dashboard"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-bone"><p className="font-serif text-2xl italic text-ink/50">Loading…</p></div>;
  if (!user) return <Login />;
  if (!user.is_admin) return <Denied onLogout={doLogout} />;

  const list = tab === "bookings" ? fBookings : fInquiries;

  return (
    <div className="min-h-screen bg-sand/30" data-testid="dashboard">
      <header className="bg-pine text-bone">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src={LOGO_MARK} alt="tpj" className="h-10" />
            <span className="font-serif text-xl">The People's <span className="italic text-terra">Josh</span></span>
          </a>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-bone/70">{user.name || user.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 text-sm border border-bone/25 px-4 py-2 hover:border-terra hover:text-terra transition-colors">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 md:px-10 py-10">
        <a href="/" className="inline-flex items-center gap-2 text-sm text-ink/50 hover:text-terra transition-colors mb-6"><ArrowLeft className="w-4 h-4" /> Back to site</a>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Consultation Requests</h1>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <div className="flex gap-2">
            <button onClick={() => setTab("bookings")} data-testid="tab-bookings"
              className={`flex items-center gap-2 px-5 py-2.5 text-sm border transition-colors ${tab === "bookings" ? "bg-terra text-white border-terra" : "border-ink/25 hover:border-terra"}`}>
              <CalendarDays className="w-4 h-4" /> Bookings ({bookings.length})
            </button>
            <button onClick={() => setTab("inquiries")} data-testid="tab-inquiries"
              className={`flex items-center gap-2 px-5 py-2.5 text-sm border transition-colors ${tab === "inquiries" ? "bg-terra text-white border-terra" : "border-ink/25 hover:border-terra"}`}>
              <Mail className="w-4 h-4" /> Inquiries ({inquiries.length})
            </button>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="dashboard-search" placeholder="Search name, email, school…"
              className="pl-9 pr-4 py-2.5 w-full sm:w-80 bg-bone border border-ink/20 outline-none focus:border-terra transition-colors text-sm" />
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {list.map((x) => (
            <Card key={x.id} testid={`row-${tab}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-2xl leading-tight">{x.name}</p>
                  <a href={`mailto:${x.email}`} className="text-sm text-terra hover:underline">{x.email}</a>
                </div>
                {tab === "bookings" && <span className="text-xs bg-pine text-bone px-2.5 py-1 whitespace-nowrap">{x.date} · {x.time}</span>}
              </div>
              {x.organisation && <p className="mt-3 text-sm text-ink/60"><span className="text-ink/40">School:</span> {x.organisation}</p>}
              {x.service && <p className="mt-1 text-sm text-ink/60"><span className="text-ink/40">Interest:</span> {x.service}</p>}
              {(x.message || x.notes) && <p className="mt-3 text-sm text-ink/75 border-t border-ink/10 pt-3">{x.message || x.notes}</p>}
              <p className="mt-3 text-xs text-ink/35">{new Date(x.created_at).toLocaleString()}</p>
            </Card>
          ))}
        </div>

        {fetched && list.length === 0 && (
          <div className="mt-16 text-center text-ink/50" data-testid="dashboard-empty">
            <p className="font-serif text-2xl italic">No {tab} {q ? "match your search" : "yet"}.</p>
          </div>
        )}
      </main>
    </div>
  );
}
